import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import {
    BorderRadiusMedium,
    BorderRadiusSmall,
    ColorNeutralBlack,
    ColorBrandBlue1,
    ColorBrandBlue5,
    ColorNordicGrey1,
    Spacing4,
    Spacing8,
    Spacing12,
    Spacing16
} from 'autogen/design-tokens/tokens';
import type { StoreParcelShopResult } from 'autogen/swagger/all';
import Radio from 'components/shared/inputs/radio';
import StockIndicator from 'components/shared/stockIndicator';
import { useTranslation } from 'react-i18next';
import * as transKey from 'autogen/translation-keys/trans-root-website';

const Wrapper = styled.div<{ showSelectParcelShop?: boolean }>`
    position: relative;

    ${({ showSelectParcelShop }) =>
        showSelectParcelShop &&
        css`
            cursor: pointer;
        `}
`;

const Container = styled.div<{ isChecked: boolean }>`
    display: grid;
    grid-template-areas: 'Address RadioContainer' 'OpeningHours AdditionalInformation';
    grid-template-columns: auto 120px;
    gap: ${Spacing12};
    border: 1px solid ${({ isChecked }) => (isChecked ? ColorBrandBlue1 : ColorNordicGrey1)};
    border-radius: ${BorderRadiusMedium};
    padding: ${Spacing16};
`;

const BadgeList = styled.div`
    position: absolute;
    top: 0;
    display: flex;
    flex-wrap: wrap;
    max-height: 16px;
    overflow: hidden;
    transform: translateY(-50%);
    padding: 0 ${Spacing16};
`;

const Badge = styled.span`
    display: inline-block;
    padding: ${Spacing4} ${Spacing8};
    color: ${ColorNeutralBlack};
    background-color: ${ColorBrandBlue5};
    border-radius: ${BorderRadiusSmall};
    font-weight: bold;
    line-height: 1em;
    font-size: 8px;
    letter-spacing: 0.05em;

    & + & {
        margin-left: ${Spacing4};
    }
`;

const Address = styled.div`
    grid-area: Address;
`;

const OpeningHours = styled.div`
    display: grid;
    grid-area: OpeningHours;
    grid-template-columns: 65px auto;
`;

const RadioContainer = styled.div`
    grid-area: RadioContainer;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
`;

const AdditionalInformation = styled.div`
    grid-area: AdditionalInformation;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    font-weight: bold;
`;

interface IParcelPickup extends StoreParcelShopResult {
    isActive?: boolean;
    selectCallback: () => void;
    showSelectParcelShop?: boolean;
}

export default function StoreItem(props: IParcelPickup): JSX.Element {
    const {
        isActive = false,
        selectCallback,
        addressLines,
        distance,
        openingHours,
        parcelShopId,
        productStockStatus,
        tags,
        showSelectParcelShop
    } = props;
    const { t } = useTranslation();

    function getStockTranslation(): string {
        const { FewInStock, IsInStock, SoldOut, UnknownStock } = transKey.ProductStoreSearch.ProductStock;

        switch (productStockStatus) {
            case 'IsInStock':
                return t(IsInStock);
            case 'FewInStock':
                return t(FewInStock);
            case 'SoldOut':
                return t(SoldOut);
            default:
                return t(UnknownStock);
        }
    }

    return (
        <Wrapper onClick={selectCallback} showSelectParcelShop={showSelectParcelShop}>
            {!!tags.length && (
                <BadgeList>
                    {tags.map((tag, i) => (
                        <Badge key={i}>{tag.text}</Badge>
                    ))}
                </BadgeList>
            )}
            <Container isChecked={!!(isActive && showSelectParcelShop)}>
                <Address>
                    {addressLines.map((addressLine) => (
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                        <div key={addressLine} dangerouslySetInnerHTML={{ __html: addressLine }} />
                    ))}
                </Address>
                {showSelectParcelShop && (
                    <RadioContainer>
                        <Radio checked={isActive} />
                    </RadioContainer>
                )}
                <OpeningHours>
                    {openingHours.map((openingHour) => (
                        <Fragment key={`${parcelShopId}_${openingHour.days}`}>
                            <span>{openingHour.days}:</span>
                            <span>{openingHour.openingHours}</span>
                        </Fragment>
                    ))}
                </OpeningHours>
                <AdditionalInformation>
                    <div>{distance}</div>
                    <div>
                        <StockIndicator checkmarkType={productStockStatus} useDot />
                        {getStockTranslation()}
                    </div>
                </AdditionalInformation>
            </Container>
        </Wrapper>
    );
}
